import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import { useTranslation } from "react-i18next";
import codService, { deriveCodReviewStatus } from "../../../services/CodService";
import {
  CodInvoiceSelection,
  CodOrder,
  CodOrderInvoices,
  CodReviewStatus,
  CodSelectedInvoice,
} from "../../../types/codSalesRep";
import { showToast } from "../../component/common/toastNotification";
import {
  flattenPayableInvoices,
  formatCodAmount,
  formatCodDate,
} from "../../../utils/codUtils";
import {
  NJIM,
  codTableHeadCellSx,
  codTableSx,
  filterSelectSx,
  iconBtnSx,
  primaryBtnSx,
} from "./codStyles";
import SearchField from "../../component/common/searchField";
import { CodCustomerOption, matchesCodCustomer } from "./codCustomerUtils";
import PostCodPaymentModal from "./PostCodPaymentModal";
import StatusChip from "../../../components/ui/StatusChip";
import EmptyState from "../../../components/ui/EmptyState";
import TableSkeleton from "../../../components/ui/TableSkeleton";
import CodSectionHeader from "../../../components/ui/CodSectionHeader";
import FilterBar from "../../../components/ui/FilterBar";

const deriveStatus = (order: CodOrder): CodReviewStatus =>
  deriveCodReviewStatus(order);

interface CodMyOrdersTabProps {
  refreshKey?: number;
  customer?: CodCustomerOption | null;
  onCountsChange?: (counts: Record<CodReviewStatus, number>) => void;
  onPosted?: () => void;
}

const CodMyOrdersTab: React.FC<CodMyOrdersTabProps> = ({
  refreshKey = 0,
  customer = null,
  onCountsChange,
  onPosted,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<CodOrder[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedId, setExpandedId] = useState<string>("");
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  // Sage invoices per order — COD cash is collected per invoice, never per order.
  const [invoiceOrders, setInvoiceOrders] = useState<CodOrderInvoices[]>([]);
  const [modalInvoices, setModalInvoices] = useState<CodSelectedInvoice[]>([]);

  const customerId = customer?.customerId ?? "";

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, invoices] = await Promise.all([
        codService.listOrders(),
        customerId
          ? codService
              .listInvoiceOrders({ customerId, includePaid: false })
              .catch(() => [] as CodOrderInvoices[])
          : Promise.resolve([] as CodOrderInvoices[]),
      ]);
      setOrders(rows);
      setInvoiceOrders(invoices);
    } catch {
      showToast(t("codSalesRep.loadFailed"), "error");
    } finally {
      setLoading(false);
    }
  }, [customerId, t]);

  // Use the API's hasPayableInvoice / totalRemaining — never derive payable amounts from the order.
  const invoiceInfoByOrderId = useMemo(() => {
    const map = new Map<string, CodOrderInvoices>();
    invoiceOrders.forEach((order) => map.set(order.orderId, order));
    return map;
  }, [invoiceOrders]);
  const canCollect = (order: CodOrder) => Boolean(invoiceInfoByOrderId.get(order.id)?.hasPayableInvoice);

  useEffect(() => {
    loadOrders();
  }, [loadOrders, refreshKey]);

  const scopedOrders = useMemo(
    () => (customer ? orders.filter((order) => matchesCodCustomer(order, customer)) : []),
    [orders, customer]
  );

  const counts = useMemo(() => {
    const base: Record<CodReviewStatus, number> = {
      pendingReview: 0,
      approved: 0,
      postedToSage: 0,
      rejected: 0,
      partlyPaid: 0,
      paid: 0,
    };
    scopedOrders.forEach((order) => {
      base[deriveStatus(order)] += 1;
    });
    return base;
  }, [scopedOrders]);

  useEffect(() => {
    onCountsChange?.(counts);
  }, [counts, onCountsChange]);

  // Same indicator as the customer Order History: green = synced to Sage X3, yellow = not yet.
  const syncDot = (order: CodOrder) => (
    <Tooltip title={order.isSynced ? t("codSalesRep.syncedToSage") : t("codSalesRep.notSyncedToSage")}>
      <CircleIcon
        aria-label={order.isSynced ? t("codSalesRep.syncedToSage") : t("codSalesRep.notSyncedToSage")}
        sx={{ color: order.isSynced ? NJIM.success : NJIM.warning, fontSize: 14, verticalAlign: "middle" }}
      />
    </Tooltip>
  );

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return scopedOrders.filter((order) => {
      const status = deriveStatus(order);
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (!keyword) return true;
      const sage = (order.salesOrderNumber || order.sageOrderNumbers?.join(" ") || "").toLowerCase();
      return (
        order.number.toLowerCase().includes(keyword) ||
        sage.includes(keyword) ||
        (order.customerName ?? "").toLowerCase().includes(keyword) ||
        (order.customerCode ?? "").toLowerCase().includes(keyword) ||
        (order.paymentNumber ?? "").toLowerCase().includes(keyword)
      );
    });
  }, [scopedOrders, search, statusFilter]);

  const statusChip = (status: CodReviewStatus) => {
    const map = {
      pendingReview: { label: t("codSalesRep.pendingReview"), color: "warning" as const },
      approved: { label: t("codSalesRep.approved"), color: "info" as const },
      postedToSage: { label: t("codSalesRep.postedToSage"), color: "success" as const },
      rejected: { label: t("codSalesRep.rejected"), color: "error" as const },
      paid: { label: t("codSalesRep.paid"), color: "success" as const },
      partlyPaid: { label: t("codSalesRep.partlyPaid"), color: "warning" as const },
    };
    const cfg = map[status];
    return <StatusChip status={status} label={cfg.label} />;
  };

  const selectedOrders = filtered.filter((order) => selectedOrderIds.includes(order.id));

  // Selected orders → their payable Sage invoices, shown one row each in the modal.
  const openPostModal = () => {
    const invoices = flattenPayableInvoices(
      invoiceOrders.filter((order) => selectedOrderIds.includes(order.orderId))
    );
    if (invoices.length === 0) {
      showToast(t("codSalesRep.noPayableInSelection"), "info");
      return;
    }
    setModalInvoices(invoices);
    setModalOpen(true);
  };

  const handlePostPayment = async (
    documentReceiptNumber: string,
    selections: CodInvoiceSelection[]
  ) => {
    if (selections.length === 0) return;
    setPosting(true);
    try {
      const result = await codService.postPayment({
        documentReceiptNumber,
        invoices: selections,
      });
      if (result.failed.length > 0) {
        showToast(t("codSalesRep.paymentPostedWithIssues"), "info");
      } else {
        showToast(
          t("codSalesRep.paymentPosted", {
            ids: result.transactionIds.join(", ") || "—",
          }),
          "success"
        );
      }
      setModalOpen(false);
      setSelectedOrderIds([]);
      await loadOrders();
      onPosted?.();
    } catch {
      // interceptor
    } finally {
      setPosting(false);
    }
  };

  if (!customer) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {t("codSalesRep.selectCustomerFirst")}
      </Alert>
    );
  }

  if (loading) {
    return <TableSkeleton rows={6} columns={6} />;
  }

  return (
    <Box>
      <CodSectionHeader
        title={t("codSalesRep.myOrders")}
        helper={t("codSalesRep.myOrdersHint")}
        action={
          selectedOrders.length > 0 ? (
            <Button variant="contained" onClick={openPostModal} sx={primaryBtnSx}>
              {t("codSalesRep.postPayment")} ({selectedOrders.length})
            </Button>
          ) : null
        }
      />
      <FilterBar>
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder={t("codSalesRep.searchOrders")}
        />
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={filterSelectSx}
        >
          <MenuItem value="all">{t("codSalesRep.allStatuses")}</MenuItem>
          <MenuItem value="pendingReview">{t("codSalesRep.pendingReview")}</MenuItem>
          <MenuItem value="approved">{t("codSalesRep.approved")}</MenuItem>
          <MenuItem value="postedToSage">{t("codSalesRep.postedToSage")}</MenuItem>
          <MenuItem value="partlyPaid">{t("codSalesRep.partlyPaid")}</MenuItem>
          <MenuItem value="paid">{t("codSalesRep.paid")}</MenuItem>
          <MenuItem value="rejected">{t("codSalesRep.rejected")}</MenuItem>
        </Select>
      </FilterBar>

      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Table size="small" sx={codTableSx}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={codTableHeadCellSx} />
              <TableCell padding="checkbox" sx={codTableHeadCellSx} />
              <TableCell padding="checkbox" sx={codTableHeadCellSx} />
              <TableCell sx={codTableHeadCellSx}>{t("codSalesRep.orderId")}</TableCell>
              <TableCell sx={codTableHeadCellSx}>{t("codSalesRep.customer")}</TableCell>
              <TableCell sx={codTableHeadCellSx}>{t("codSalesRep.submitted")}</TableCell>
              <TableCell align="right" sx={codTableHeadCellSx}>{t("common.total")}</TableCell>
              <TableCell sx={codTableHeadCellSx}>{t("common.status")}</TableCell>
              <TableCell align="right" sx={codTableHeadCellSx}>{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ border: 0 }}>
                  <EmptyState
                    title={t("codSalesRep.emptyOrdersTitle")}
                    description={t("codSalesRep.emptyOrdersDesc")}
                  />
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((order) => {
                const status = deriveStatus(order);
                const isOpen = expandedId === order.id;
                return (
                  <React.Fragment key={order.id}>
                    <TableRow hover>
                      <TableCell padding="checkbox" sx={{ textAlign: "center" }}>
                        {syncDot(order)}
                      </TableCell>
                      <TableCell padding="checkbox">
                        {canCollect(order) ? (
                          <Checkbox
                            checked={selectedOrderIds.includes(order.id)}
                            onChange={() =>
                              setSelectedOrderIds((prev) =>
                                prev.includes(order.id)
                                  ? prev.filter((id) => id !== order.id)
                                  : [...prev, order.id]
                              )
                            }
                          />
                        ) : null}
                      </TableCell>
                      <TableCell padding="checkbox">
                        <Tooltip title={isOpen ? t("codSalesRep.collapseDetails") : t("codSalesRep.expandDetails")}>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedId(isOpen ? "" : order.id)}
                            aria-label={isOpen ? t("codSalesRep.collapseDetails") : t("codSalesRep.expandDetails")}
                            sx={iconBtnSx}
                          >
                            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {order.number}
                        </Typography>
                        {order.sageOrderNumbers?.[0] && (
                          <Typography variant="caption" color="primary">
                            {order.salesOrderNumber || order.sageOrderNumbers[0]}
                          </Typography>
                        )}
                        {order.paymentNumber && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {t("codSalesRep.paymentNumber")}: {order.paymentNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} color={NJIM.textPrimary}>
                          {order.customerCode || "—"}
                        </Typography>
                        {order.customerName && order.customerName !== order.customerCode && (
                          <Typography variant="caption" color="text.secondary">
                            {order.customerName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatCodDate(order.date)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: NJIM.textPrimary, fontVariantNumeric: "tabular-nums" }}>
                        {formatCodAmount(order.amount, order.currency)}
                        {canCollect(order) && (
                          <Typography variant="caption" display="block" color={NJIM.danger}>
                            {t("codSalesRep.remaining")}:{" "}
                            {formatCodAmount(invoiceInfoByOrderId.get(order.id)?.totalRemaining ?? 0, order.currency)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{statusChip(status)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={t("common.viewDetails")}>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedId(isOpen ? "" : order.id)}
                            aria-label={t("common.viewDetails")}
                            sx={iconBtnSx}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 3, py: 2, background: NJIM.muted }}>
                            {order.rejectionReason && (
                              <Typography
                                variant="body2"
                                color="error.main"
                                sx={{ mb: 1.5, fontWeight: 600 }}
                              >
                                {t("codSalesRep.rejectionReason")}: {order.rejectionReason}
                              </Typography>
                            )}
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>{t("common.item")}</TableCell>
                                  <TableCell align="right">{t("common.quantity")}</TableCell>
                                  <TableCell align="right">{t("common.unitPrice")}</TableCell>
                                  <TableCell align="right">{t("common.total")}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(order.items ?? []).map((item, idx) => (
                                  <TableRow key={`${order.id}-${idx}`}>
                                    <TableCell>{item.name}</TableCell>
                                    <TableCell align="right">{item.quantity}</TableCell>
                                    <TableCell align="right">
                                      {formatCodAmount(item.price, order.currency)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {formatCodAmount(
                                        item.grossPrice ?? item.price * item.quantity,
                                        order.currency
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.5 }}>
        {filtered.length === 0 ? (
          <EmptyState
            title={t("codSalesRep.emptyOrdersTitle")}
            description={t("codSalesRep.emptyOrdersDesc")}
          />
        ) : (
          filtered.map((order) => {
            const status = deriveStatus(order);
            const isOpen = expandedId === order.id;
            return (
              <Box
                key={order.id}
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  border: `1px solid ${NJIM.border}`,
                  background: NJIM.white,
                  boxShadow: NJIM.elevation,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                  {canCollect(order) ? (
                    <Checkbox
                      checked={selectedOrderIds.includes(order.id)}
                      onChange={() =>
                        setSelectedOrderIds((prev) =>
                          prev.includes(order.id)
                            ? prev.filter((id) => id !== order.id)
                            : [...prev, order.id]
                        )
                      }
                    />
                  ) : null}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {syncDot(order)}
                      <Typography sx={{ fontWeight: 700, color: NJIM.ink }}>{order.number}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                      {order.customerName || order.customerCode} · {formatCodDate(order.date)}
                    </Typography>
                    <Typography sx={{ mt: 0.75, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                      {formatCodAmount(order.amount, order.currency)}
                    </Typography>
                    <Box sx={{ mt: 1 }}>{statusChip(status)}</Box>
                  </Box>
                  <Tooltip title={isOpen ? t("codSalesRep.collapseDetails") : t("codSalesRep.expandDetails")}>
                    <IconButton
                      size="small"
                      onClick={() => setExpandedId(isOpen ? "" : order.id)}
                      aria-label={isOpen ? t("codSalesRep.collapseDetails") : t("codSalesRep.expandDetails")}
                      sx={iconBtnSx}
                    >
                      {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Collapse in={isOpen} timeout={180} unmountOnExit>
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${NJIM.border}` }}>
                    {(order.items ?? []).map((item, idx) => (
                      <Box
                        key={`${order.id}-m-${idx}`}
                        sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.75 }}
                      >
                        <Typography sx={{ fontSize: 13 }}>{item.name} × {item.quantity}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                          {formatCodAmount(item.grossPrice ?? item.price * item.quantity, order.currency)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>
      <PostCodPaymentModal
        open={modalOpen}
        invoices={modalInvoices}
        loading={posting}
        onClose={() => setModalOpen(false)}
        onSubmit={handlePostPayment}
        onRemoveInvoice={(invoiceId) =>
          setModalInvoices((prev) => prev.filter((invoice) => invoice.invoiceId !== invoiceId))
        }
      />
    </Box>
  );
};

export default CodMyOrdersTab;
